import { VideoTranscriptElement } from '../models/elements/VideoTranscriptElement';
import { EditorChangeType } from '../types';
import { AbstractElementCommand } from './AbstractCommand';

import type { Element } from '../models/Element';
import type { VideoEditor } from '../VideoEditor';

export class SplitElementCommand extends AbstractElementCommand {
  private splitTime: number;

  getName(): string {
    return 'SplitElementCommand';
  }
  private firstResultElement: Element | null = null;
  private secondResultElement: Element | null = null;
  private trackId: string | null | undefined = null;

  constructor(
    editor: VideoEditor,
    element: Element,
    splitTime: number,
    snapToFrame: boolean = true,
  ) {
    super(editor, element);
    this.splitTime = snapToFrame ? this.snapTimeToFrame(splitTime) : splitTime;
  }

  execute(): boolean {
    // Store the track for undo
    this.trackId = this.editor.timeline.getTrackIdForElement(
      this.sourceElement.id,
    );
    if (!this.trackId) return false;

    const result = this.performSplitOperation();
    if (!result) return false;

    // Store the resulting elements for undo and notification
    [this.firstResultElement, this.secondResultElement] = result;

    // Recalculate timeline duration
    this.recalculateTimeline();

    // Notify observers about the split
    this.editor.notifyObservers(EditorChangeType.ELEMENT_SPLIT, {
      originalElement: this.sourceElement,
      firstElement: this.firstResultElement,
      secondElement: this.secondResultElement,
      splitTime: this.splitTime,
    });

    return true;
  }

  undo(): boolean {
    if (
      !this.firstResultElement ||
      !this.secondResultElement ||
      !this.trackId
    ) {
      return false;
    }

    // Remove both split elements
    this.editor.timeline.removeElement(this.firstResultElement.id);
    this.editor.timeline.removeElement(this.secondResultElement.id);

    // Restore the original element
    this.editor.timeline.addElementToTrack(this.trackId, this.sourceElement);

    // Recalculate timeline duration
    this.recalculateTimeline();

    // Notify observers about the undo
    this.editor.notifyObservers(EditorChangeType.ELEMENT_SPLIT_UNDONE, {
      originalElement: this.sourceElement,
      firstElement: this.firstResultElement,
      secondElement: this.secondResultElement,
      splitTime: this.splitTime,
    });

    return true;
  }

  /**
   * Performs the split operation using the appropriate track manager
   * @returns Array with split result elements, or null if failed
   */
  private performSplitOperation(): [Element, Element] | null {
    if (this.isTranscriptElement()) {
      return this.splitTranscriptElement();
    } else {
      return this.splitRegularElement();
    }
  }

  /**
   * Checks if the source element is a VideoTranscriptElement
   */
  private isTranscriptElement(): boolean {
    return this.sourceElement instanceof VideoTranscriptElement;
  }

  /**
   * Splits a VideoTranscriptElement using TranscriptTrackManager
   */
  private splitTranscriptElement(): [Element, Element] | null {
    const transcriptTrackManager = this.editor.getTranscriptTrackManager();
    return transcriptTrackManager.splitElement(
      this.sourceElement as VideoTranscriptElement,
      this.splitTime,
    );
  }

  /**
   * Splits a regular element using TrackManager
   */
  private splitRegularElement(): [Element, Element] | null {
    const trackManager = this.editor.getTrackManager();
    return trackManager.splitElement(
      this.sourceElement,
      this.splitTime,
    );
  }

  getDescription(): string {
    return `Split element ${this.sourceElement.id} at ${this.splitTime}s`;
  }

  // Expose the result elements for commandSplitElement to return
  getResultElements(): [Element, Element] | null {
    if (!this.firstResultElement || !this.secondResultElement) {
      return null;
    }
    return [this.firstResultElement, this.secondResultElement];
  }
}
